<template>
 <div class="documents-modal">
  <div class="modal-header">
   <div>
    <h2>Driver Documents</h2>
    <div class="subtitle">
     <span>{{ subtitle }}</span>
     <span v-if="driverDetails.token" class="active-pill" :class="{ 'active-pill--inactive': driverDetails.trash }"
      ><span class="dot"></span>{{ driverDetails.trash ? "Inactive" : "Active" }}</span
     >
    </div>
   </div>
   <button type="button" class="close-btn" title="Close" @click="cancelSubmit"><i class="fas fa-times"></i></button>
  </div>

  <div class="modal-body">
   <div class="documents-grid" v-if="token">
    <DocumentCard
     v-for="doc in documentConfig"
     :key="doc.key"
     :title="doc.title"
     :accept="doc.accept"
     :url="documentField(doc.key, 'image')"
     :status="documentField(doc.key, 'verified')"
     :uploading="uploadingKey === doc.key"
     @preview="previewImage"
     @upload="uploadDocument(doc, $event)"
    ></DocumentCard>
   </div>
   <div v-else class="no-documents">No data Available</div>
  </div>

  <div class="modal-footer">
   <span>{{ uploadedCount }} of {{ documentConfig.length }} documents uploaded</span>
   <v-btn depressed class="footer-close" @click="cancelSubmit">Close</v-btn>
  </div>

  <!-- Image Preview Dialog -->
  <vue-easy-lightbox :visible="imagePreviewDialog" :imgs="[imageToPreview]" @hide="imagePreviewDialog = false" v-if="!isPdf(imageToPreview)"></vue-easy-lightbox>

  <v-dialog v-model="imagePreviewDialog" class="preview-board" width="700px" max-height="100%" v-if="isPdf(imageToPreview)">
   <iframe width="100%" :src="imageToPreview" style="height: 100vh; overflow: auto"></iframe>
  </v-dialog>
  <Loading v-if="loading"></Loading>
 </div>
</template>

<script>
import axios from "@/axios/axios-global";
import Loading from "@/components/common/Loading";
import DocumentCard from "@/components/common/DocumentCard.vue";
import { encryptUtil } from "../../../Utils/decrypt";

// key: field in driver document, imageName: value the update API expects, expdate: send the stored expiry with the upload
const DOCUMENTS = [
 { key: "photo", title: "Photo", imageName: "photo", expdate: false, accept: "image/*" },
 { key: "idproof", title: "ID Proof", imageName: "idProof", expdate: true, accept: "image/*,application/pdf" },
 { key: "licensefront", title: "Licence Front", imageName: "licenceFront", expdate: true, accept: "image/*,application/pdf" },
 { key: "licenseback", title: "Licence Back", imageName: "licenceBack", expdate: false, accept: "image/*,application/pdf" },
 { key: "policeclearance", title: "Police Clearance", imageName: "policeClearance", expdate: true, accept: "image/*,application/pdf" },
];

export default {
 name: "DocumentInsert",
 components: { Loading, DocumentCard },
 props: {
  token: String,
  resetTrigger: String,
 },
 data: () => {
  return {
   documentConfig: DOCUMENTS,
   documents: {},
   driverDetails: {},
   loading: false,
   uploadingKey: "",
   imageToPreview: "",
   imagePreviewDialog: false,
  };
 },
 computed: {
  subtitle() {
   return [this.driverDetails.name, this.driverDetails.mobile].filter(Boolean).join(" · ");
  },
  uploadedCount() {
   return DOCUMENTS.filter((doc) => this.documentField(doc.key, "image")).length;
  },
 },
 mounted() {
  this.getDriverDetails();
 },
 methods: {
  isPdf(url) {
   return url && url.toLowerCase().includes(".pdf");
  },
  documentField(key, field) {
   const doc = this.documents && this.documents[key];
   if (!doc) return field === "image" ? "" : null;
   const value = doc[field];
   if (field === "image") return value || "";
   return value === undefined ? null : value;
  },
  previewImage(imageSrc) {
   this.imageToPreview = imageSrc;
   this.imagePreviewDialog = true;
  },
  async getDriverDetails() {
   if (!this.token) return;
   this.loading = true;
   try {
    const response = await axios.post("driver/details", { driverId: this.token });
    this.driverDetails = response.data || {};
    this.documents = (response.data && response.data.document) || {};
   } catch (error) {
    console.error(error);
   } finally {
    this.loading = false;
   }
  },
  async uploadDocument(doc, file) {
   this.uploadingKey = doc.key;
   try {
    const payload = {
     driverId: this.token,
     imageName: doc.imageName,
    };
    if (doc.expdate) payload.expdate = this.documentField(doc.key, "expdate") || "";

    const formData = new FormData();
    const setencryptUtil = new encryptUtil({ decrypt: payload });
    formData.append("data", setencryptUtil.getEncryptedData());
    formData.append("image", file);
    const response = await axios.patch("driver/document/update", formData);
    if (response.status === 200) {
     setTimeout(async () => {
      this.$emit("recalling");
      await this.getDriverDetails();
     }, 500);
     this.$toasted.show(response.data.message, { position: "bottom-center", duration: 4000 });
    } else {
     this.$toasted.show(response.data, { position: "bottom-center", duration: 4000 });
    }
   } catch (error) {
    console.error(error);
    this.$toasted.show("Document upload failed. Please try again.", { position: "bottom-center", duration: 4000 });
   } finally {
    this.uploadingKey = "";
   }
  },
  cancelSubmit() {
   this.$emit("cancel-insert");
  },
 },
};
</script>

<style scoped lang="scss">
@import "src/components/common/documentsModal";
</style>
